/* ──────────────────────────────────────────────────────────────────────────
   صفحه‌بندی یک‌دست برای همهٔ فهرست‌های برنامه.

   قرارداد پروژه:
     * هر بار فقط ۲۰۰ ردیف از سرور گرفته می‌شود (OFFSET/FETCH در بک‌اند).
     * پاسخ همهٔ فهرست‌ها: {ok, count, total, has_more, data}
     * فرانت با ?page=n صفحهٔ بعد را می‌گیرد و به انتهای لیست اضافه می‌کند.

   استفاده در یک صفحه:

     const pg = usePager();                       // state صفحه‌بندی

     const load = useCallback(async (opt = {}) => {
       const page = opt.page ?? 1;
       setLoading(true);
       const j = await API(`/x/?search=${...}&page=${page}`);
       setLoading(false);
       if (!j.ok) { setError(j.msg); return; }
       setData(prev => pg.merge(prev, j, page));   // صفحهٔ ۱ جایگزین، بقیه اضافه
     }, [...]);

   و در JSX:
     <CountBadge shown={data.length} total={pg.total} loading={loading} />
     ...
     <MoreButton pager={pg} loading={loading} onMore={p => load({ page: p })} />
   ────────────────────────────────────────────────────────────────────────── */
import React, { useState, useCallback, useRef } from 'react';

const toFa = n => String(n ?? '').replace(/[0-9]/g, d => '۰۱۲۳۴۵۶۷۸۹'[d]);

export const PAGE_SIZE = 200;

/* همان countBadge در ui — اینجا مستقیم نوشته شده تا paging.js به ListPage
   وابسته نباشد (ListPage خودش از این فایل MoreButton را می‌گیرد؛ ایمپورت
   دوطرفه حلقه می‌ساخت). */
const badgeStyle = {
  padding: '5px 12px', background: '#1a2f50', color: '#C8A84B',
  borderRadius: '20px', fontSize: '12px', fontWeight: '700', alignSelf: 'flex-end',
};

/** state و منطق صفحه‌بندی — در هر صفحهٔ فهرست یک‌بار صدا زده می‌شود. */
export function usePager() {
  const [total, setTotal]     = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage]       = useState(1);

  /** نتیجهٔ سرور را با لیست فعلی ترکیب می‌کند و شمارنده‌ها را به‌روز می‌کند. */
  const merge = useCallback((prev, j, p) => {
    setTotal(j.total ?? (j.data || []).length);
    setHasMore(!!j.has_more);
    setPage(p);
    return p > 1 ? [...prev, ...(j.data || [])] : (j.data || []);
  }, []);

  const reset = useCallback(() => { setTotal(0); setHasMore(false); setPage(1); }, []);

  return { total, hasMore, page, merge, reset, next: page + 1 };
}

/**
 * هوک کاملِ فهرست صفحه‌بندی‌شده — داده، وضعیت بارگذاری، خطا و صفحه‌بندی را
 * با هم نگه می‌دارد تا هر صفحهٔ جدید فقط یک خط بنویسد.
 *
 *   const L = usePagedList(
 *     (page, opt) => API(`/contracts/?page=${page}&search=${opt.search || ''}`),
 *   );
 *
 *   useEffect(() => { L.reload(); }, []);          // بار اول
 *   ...
 *   <ListPage ... pager={L.pager} onMore={L.more} loading={L.loading}
 *             error={L.error} unit="قرارداد">
 *     {L.data.map(...)}
 *   </ListPage>
 *
 * fetcher باید پاسخ استاندارد `{ok, count, total, has_more, data}` برگرداند.
 * `reload(opt)` صفحهٔ ۱ را می‌گیرد و لیست را جایگزین می‌کند؛
 * `more(page)` صفحهٔ بعد را می‌گیرد و به انتهای لیست اضافه می‌کند.
 * `opt` آخرین بار در هوک نگه داشته می‌شود تا `more` همان فیلترها را ببرد.
 */
export function usePagedList(fetcher) {
  const [data, setData]       = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const pager = usePager();
  const optRef = useRef({});
  const fetchRef = useRef(fetcher);
  fetchRef.current = fetcher;

  const load = useCallback(async (page = 1, opt) => {
    if (opt !== undefined) optRef.current = opt || {};
    setLoading(true); setError('');
    let j;
    try {
      j = await fetchRef.current(page, optRef.current);
    } catch (e) {
      setLoading(false); setError('خطای ارتباط با سرور'); return null;
    }
    setLoading(false);
    if (!j || !j.ok) { setError((j && j.msg) || 'خطای نامشخص'); return j; }
    setData(prev => pager.merge(prev, j, page));
    return j;
  }, [pager]);

  const reload = useCallback((opt) => load(1, opt || {}), [load]);
  const more   = useCallback((page) => load(page), [load]);

  return { data, setData, loading, error, setError, pager, load, reload, more };
}

/** «۲۰۰ از ۴۱۱» — نشانگر تعداد، یک‌شکل در همهٔ فهرست‌ها. */
export function CountBadge({ shown, total, loading, unit = 'مورد' }) {
  if (loading || !total) return null;
  return (
    <div style={badgeStyle}>
      {shown < total ? `${toFa(shown)} از ${toFa(total)}` : `${toFa(total)} ${unit}`}
    </div>
  );
}

/**
 * دکمهٔ «نمایش بیشتر» — در نوار ثابت پایین صفحه (prop `footer` در ListPage)
 * نمایش داده می‌شود، پس همیشه بدون اسکرول در دید است.
 *
 * عمداً IntersectionObserver ندارد: چون دکمه همیشه روی صفحه است، هر
 * observerی بلافاصله آتش می‌کرد و همهٔ صفحات را پشت‌سرهم می‌گرفت (روی جدول
 * ۱۶ هزار ردیفی یعنی دانلود کل جدول). بارگذاری فقط با کلیک کاربر است.
 *
 * وقتی ردیف دیگری نمانده باشد، به‌جای دکمه تعداد کل نشان داده می‌شود.
 */
export function MoreButton({ pager, loading, onMore, unit = 'ردیف' }) {
  if (!pager || !pager.total) return null;

  if (!pager.hasMore) {
    return (
      <span style={{ fontSize: '12px', color: '#777' }}>
        همهٔ {toFa(pager.total)} {unit} نمایش داده شد
      </span>
    );
  }

  const shown     = pager.page * PAGE_SIZE;
  const remaining = Math.max(pager.total - shown, 0);
  const next      = Math.min(PAGE_SIZE, remaining);

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
      <span style={{ fontSize: '12px', color: '#555' }}>
        {toFa(shown)} از {toFa(pager.total)} {unit}
      </span>
      <button
        onClick={() => onMore(pager.page + 1)}
        disabled={loading}
        style={{
          padding: '7px 20px',
          background: loading ? '#ccc' : 'linear-gradient(135deg,#1a237e,#283593)',
          color: '#fff', border: 'none', borderRadius: '8px',
          cursor: loading ? 'default' : 'pointer',
          fontFamily: 'inherit', fontSize: '12.5px', fontWeight: 700,
        }}
      >
        {loading ? '⏳ در حال بارگذاری…' : `▼ ${toFa(next)} ${unit} بعدی`}
      </button>
      <span style={{ fontSize: '11.5px', color: '#999' }}>
        ({toFa(remaining)} باقی‌مانده)
      </span>
    </div>
  );
}
